import { useState, useContext, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Text,
  VStack,
  Button,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  Spinner,
  Flex,
  Divider,
  Link,
  Center,
} from '@chakra-ui/react';
import { LockIcon, WarningIcon, CheckIcon } from '@chakra-ui/icons';
import {
  useStripe,
  useElements,
  AddressElement,
  PaymentElement,
} from '@stripe/react-stripe-js';
import { HttpContext } from '../../../context/HttpContext';
import { useNavigate } from 'react-router';
import TaxIdSelect from '../../common/TaxIdSelect';
import { useBillingService } from '../../../services/billingService';
import moment from 'moment';
import {
  formatGbpFromMinorUnits,
  monthlyAmountMinorUnits,
} from '../../../utils/subscriptionUtils';
import StripeElementsProvider from './StripeElementsProvider';
import { STRIPE_APPEARANCE } from './stripeAppearance';

function BillingFormFields({
  selectedPlan,
  setParentLoading,
  companyName,
  accountEmail,
  isRefreshingSetupIntent,
  refreshSetupIntent,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { authAxios } = useContext(HttpContext);
  const navigate = useNavigate();

  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessingCard, setIsProcessingCard] = useState(false);
  const [isCreatingSubscription, setIsCreatingSubscription] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const [taxIdData, setTaxIdData] = useState({ type: '', value: '' });
  const [taxIdValidation, setTaxIdValidation] = useState({
    isValid: null,
    message: '',
    hasBlockingErrors: false,
  });

  const [elementStates, setElementStates] = useState({
    address: { complete: false, empty: true },
    paymentElement: { complete: false, empty: true },
  });

  const handleTaxIdChange = (newTaxIdData) => setTaxIdData(newTaxIdData);
  const handleTaxIdValidationChange = (validationResult) =>
    setTaxIdValidation(validationResult);

  const handleElementChange = (elementType) => (event) => {
    setElementStates((prev) => ({
      ...prev,
      [elementType]: {
        complete: event.complete,
        empty: event.empty,
        error: event.error,
      },
    }));
  };

  const isFormValid = () => {
    const isTaxIdIncomplete = taxIdData.type && !taxIdData.value;
    return (
      stripe &&
      !isLoading &&
      !isRefreshingSetupIntent &&
      elementStates.address.complete &&
      elementStates.paymentElement.complete &&
      !isTaxIdIncomplete
    );
  };

  const hasValidationErrors = () => {
    if (taxIdData.value && taxIdValidation.isValid === false) return true;
    return taxIdValidation.hasBlockingErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setParentLoading(true);
    setErrorMessage('');
    setIsSuccess(false);

    try {
      if (
        taxIdData.value &&
        taxIdValidation.isValid === false &&
        taxIdValidation.hasBlockingErrors
      ) {
        throw new Error(
          'Please enter a valid tax ID format. This tax ID will be verified against government databases.'
        );
      }

      if (!stripe || !elements) {
        throw new Error('Stripe has not been initialized.');
      }

      const addressElement = elements.getElement(AddressElement);
      const { value: addressDetails } = await addressElement.getValue();

      setIsProcessingCard(true);

      const billingDetails = {
        name: addressDetails.name,
        email: accountEmail,
        phone: addressDetails.phone,
        address: addressDetails.address,
      };

      const { error: setupError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: {
            billing_details: billingDetails,
          },
        },
        redirect: 'if_required',
      });

      if (setupError) {
        throw new Error(setupError.message);
      }

      setIsProcessingCard(false);
      setIsCreatingSubscription(true);

      await authAxios.post('/internal/v1/billing/subscriptions', {
        plan_id: selectedPlan.id,
        price_id: selectedPlan.selectedPrice.id,
        payment_method_id: setupIntent.payment_method,
        billing_details: billingDetails,
        ...(taxIdData.type &&
          taxIdData.value && {
            tax_id: taxIdData.value,
            tax_id_type: taxIdData.type,
          }),
      });

      setIsSuccess(true);

      setTimeout(() => {
        navigate('/dashboard');
        window.location.reload();
      }, 2000);
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.response?.data?.errors?.[0]?.error ||
        err.message ||
        'Something went wrong. Please try again.';

      setErrorMessage(message);

      if (
        err.message?.includes('setup_intent') ||
        err.message?.includes('already been confirmed')
      ) {
        refreshSetupIntent();
      }
    } finally {
      setIsLoading(false);
      setParentLoading(false);
      setIsProcessingCard(false);
      setIsCreatingSubscription(false);
    }
  };

  if (isSuccess) {
    return (
      <Center h="80vh">
        <Box
          spacing={6}
          p={8}
          bg="white"
          borderRadius="lg"
          position="relative"
          _before={{
            content: '""',
            position: 'absolute',
            inset: 0,
            padding: '3px',
            borderRadius: 'inherit',
            background: 'linear-gradient(180deg, #FF9554 16%, #FF4935 100%)',
            WebkitMask:
              'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
            pointerEvents: 'none',
          }}
        >
          <Flex direction="column" alignItems="center" gap={4}>
            <Box p={3} borderRadius="full" fontSize="3xl">
              <CheckIcon boxSize={10} color="orangePrimary" />
            </Box>
            <Text textStyle="headingFamilySemiBold" fontSize="24px">
              Subscription activated!
            </Text>
            <Text fontSize="14px" textAlign="center">
              Your subscription is now active. You will be redirected to your
              dashboard shortly.
            </Text>
            <Spinner color="orangePrimary" size="lg" />
          </Flex>
        </Box>
      </Center>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <VStack alignItems="center" w="100%" mt={8}>
        <Text
          textStyle="headingFamilyRegular"
          fontSize={['32px', '48px']}
          color="#252525"
          lineHeight="120%"
        >
          Activate your{' '}
          <Text as="span" textStyle="headingFamilyBold">
            free {selectedPlan?.trialDays || 14}-day trial
          </Text>
        </Text>
        <Text textStyle="bodyFamilyRegular" fontSize="20px" color="#101828">
          You won’t be charged until your trial ends.
        </Text>

        <Flex direction="column" w="100%" mt={8}>
          <Text
            textStyle="headingFamilySemiBold"
            fontSize={['20px', '24px']}
            color="#101828"
          >
            Company details
          </Text>
          <Text fontSize={['10px', '12px']} color="orangePrimary" ml={1}>
            All fields required
          </Text>
          <FormControl mt={4} mb={0}>
            <Box
              mb={0}
              sx={{
                '& > *': {
                  marginBottom: '0 !important',
                },
                '@media (max-width: 768px)': {
                  marginBottom: '-35px !important',
                  '& > *': {
                    marginBottom: '0 !important',
                  },
                },
              }}
            >
              <AddressElement
                options={{
                  mode: 'billing',
                  fields: { phone: 'always' },
                  validation: { phone: { required: 'always' } },
                  display: { name: 'organization' },
                  defaultValues: {
                    name: companyName || '',
                  },
                }}
                onChange={handleElementChange('address')}
              />
            </Box>
            {elementStates.address.error && (
              <Text color="red.500" fontSize="sm" mt={3} ml={2}>
                {elementStates.address.error.message}
              </Text>
            )}
          </FormControl>
          <Flex
            direction="row"
            gap={1}
            w="100%"
            mt={['-15px', 3]}
            align="center"
            sx={{
              '@media (max-width: 768px)': {
                marginTop: '-15px',
              },
            }}
          >
            <WarningIcon
              color="#989898"
              w={['10px', '12px']}
              h={['10px', '12px']}
            />
            <Text
              color="#989898"
              fontSize={['12px', '14px']}
              textStyle="headingFamilyMedium"
              mt={0.5}
            >
              VAT is calculated based on your billing contact address, not the
              VAT ID.
            </Text>
          </Flex>
        </Flex>
        <Flex direction="column" w="100%" mt={4}>
          <Text
            textStyle="headingFamilySemiBold"
            fontSize={['20px', '24px']}
            color="#101828"
          >
            Payment details
          </Text>
          <Text textStyle="bodyFamilyRegular" fontSize="14px" color="#101828">
            We ask for your card to ensure uninterrupted access after the trial.{' '}
            <Text as="span" textStyle="bodyFamilyBold">
              You won’t be charged during the trial.
            </Text>
          </Text>
          <FormControl mt={4} isRequired>
            <FormLabel color="#4C4C4C">Card or payment method</FormLabel>
            <PaymentElement
              options={{ fields: { billingDetails: 'never' } }}
              onChange={handleElementChange('paymentElement')}
            />
            {elementStates.paymentElement.error && (
              <Text color="red.500" fontSize="sm" mt={2}>
                {elementStates.paymentElement.error.message}
              </Text>
            )}
          </FormControl>
          <Flex direction="row" gap={1} w="100%" align="center" mt={2}>
            <LockIcon
              color="#989898"
              w={['10px', '14px']}
              h={['10px', '14px']}
            />
            <Text
              color="#989898"
              fontSize={['12px', '14px']}
              textStyle="headingFamilyMedium"
              mt={1}
            >
              Secured with SSL
            </Text>
          </Flex>
        </Flex>
        <Flex direction="column" gap={3} w="100%" mt={4}>
          <Text
            textStyle="headingFamilySemiBold"
            fontSize={['20px', '24px']}
            color="#101828"
          >
            Tax ID (optional)
          </Text>

          {/* Tax ID Section */}
          <TaxIdSelect
            type={taxIdData.type}
            value={taxIdData.value}
            onChange={handleTaxIdChange}
            onValidationChange={handleTaxIdValidationChange}
          />
        </Flex>
        <Box
          position="relative"
          borderRadius="10px"
          py={{ base: 6, md: 8, lg: 10 }}
          px={{ base: 6, md: 8, lg: 12 }}
          bg="white"
          w={['100%', '100%']}
          h="100%"
          mt={8}
          minW="200px"
          _before={{
            content: '""',
            position: 'absolute',
            inset: 0,
            padding: '3px',
            borderRadius: 'inherit',
            background: 'linear-gradient(180deg, #FF9554 16%, #FF4935 100%)',
            WebkitMask:
              'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
            pointerEvents: 'none',
          }}
        >
          <Text
            textStyle="headingFamilyMedium"
            fontSize={['22px', '24px']}
            color="#101828"
            mb={{ base: 2, md: 1 }}
          >
            Summary
          </Text>
          <Text fontSize={['14px']} color="#4C4C4C">
            Your {selectedPlan?.trialDays || 14} day free trial will start
            today.{' '}
            <Text as="span" textStyle="bodyFamilyBold" color="#4C4C4C">
              You won't be charged until the trial ends.
            </Text>
          </Text>
          <Flex direction="row" w="100%" justifyContent="space-between" mt={6}>
            <Text fontSize={['14px', '16px']} color="#4C4C4C">
              Total due today
            </Text>
            <Text
              fontSize={['22px', '24px']}
              textStyle="headingFamilyBold"
              color="#FF4935"
            >
              FREE TRIAL £0.00
            </Text>
          </Flex>
          <Divider
            my={3}
            borderColor="transparent"
            height="1px"
            background="linear-gradient(180deg, #FF9554 16%, #FF4935 100%)"
          />
          <Flex direction="row" w="100%" justifyContent="space-between">
            <Text fontSize={['14px', '16px']} color="#4C4C4C">
              Trial ends
            </Text>
            <Text fontSize={['14px', '16px']} color="#4C4C4C">
              {moment()
                .add(selectedPlan?.trialDays || 14, 'day')
                .format('DD/MM/YYYY')}
            </Text>
          </Flex>
          <Divider
            my={3}
            borderColor="transparent"
            height="1px"
            background="linear-gradient(180deg, #FF9554 16%, #FF4935 100%)"
          />
          <Flex direction="row" w="100%" justifyContent="space-between">
            <Text fontSize={['14px', '16px']} color="#4C4C4C">
              Renewal price
            </Text>
            <Flex
              direction="column"
              alignItems="flex-end"
              gap={0.5}
              fontSize={['14px', '16px']}
              color="#4C4C4C"
            >
              {selectedPlan?.selectedPrice?.interval === 'year' ? (
                <>
                  <Text>
                    {formatGbpFromMinorUnits(
                      selectedPlan.selectedPrice.amount,
                      { wholePounds: true }
                    )}
                  </Text>
                  <Text fontSize="12px" color="#64748B" textAlign="right">
                    Billed annually after trial
                  </Text>
                </>
              ) : (
                <Text>
                  {formatGbpFromMinorUnits(
                    monthlyAmountMinorUnits(selectedPlan?.selectedPrice),
                    { wholePounds: true }
                  )}
                  /mo
                </Text>
              )}
            </Flex>
          </Flex>

          {errorMessage && (
            <Alert status="error" borderRadius="md" mt={6}>
              <AlertIcon />
              {errorMessage}
            </Alert>
          )}

          <Button
            type="submit"
            bg="linear-gradient(180deg, #FF9554 16%, #FF4935 100%)"
            color="white"
            w="100%"
            mt={8}
            cursor="pointer"
            _hover={{
              opacity: 0.9,
            }}
            isLoading={isLoading || isRefreshingSetupIntent}
            loadingText={
              isRefreshingSetupIntent
                ? 'Refreshing...'
                : isProcessingCard
                  ? 'Processing Payment...'
                  : isCreatingSubscription
                    ? 'Activating Subscription...'
                    : 'Processing...'
            }
            isDisabled={
              !stripe || isLoading || hasValidationErrors() || !isFormValid()
            }
          >
            Start {selectedPlan?.trialDays || 14} day free trial
          </Button>
          {!isFormValid() && !isLoading && (
            <Text fontSize="sm" color="gray.500" textAlign="center" mt={3}>
              {isRefreshingSetupIntent
                ? 'Refreshing payment form...'
                : taxIdData.type && !taxIdData.value
                  ? 'Please enter a VAT/Tax ID or select "None" to continue'
                  : 'Please complete all required fields to continue'}
            </Text>
          )}
        </Box>

        {hasValidationErrors() && (
          <Text fontSize="sm" color="red.500" textAlign="center">
            Please correct the tax ID format before continuing.
          </Text>
        )}

        <Flex justifyContent="center" mt={8}>
          <Text fontSize={['12px', '14px']} color="#252525" textAlign="center">
            By continuing with your free trial, you agree to the{' '}
            <Link
              href="https://passentry.com/terms-of-service"
              textDecoration="underline"
              isExternal
            >
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link
              href="https://passentry.com/privacy-policy"
              textDecoration="underline"
              isExternal
            >
              Privacy Policy
            </Link>
          </Text>
        </Flex>
      </VStack>
    </form>
  );
}

function BillingForm({
  selectedPlan,
  setIsLoading: setParentLoading,
  companyName,
  accountEmail,
}) {
  const billingService = useBillingService();
  const [clientSecret, setClientSecret] = useState('');
  const [setupIntentStatus, setSetupIntentStatus] = useState('');
  const [isRefreshingSetupIntent, setIsRefreshingSetupIntent] = useState(false);
  const [fetchError, setFetchError] = useState('');

  const clientSecretRef = useRef(clientSecret);
  const setupIntentStatusRef = useRef(setupIntentStatus);

  const fetchSetupIntent = useCallback(
    async (force = false) => {
      if (
        !force &&
        clientSecretRef.current &&
        setupIntentStatusRef.current === 'requires_payment_method'
      ) {
        return;
      }
      setIsRefreshingSetupIntent(true);
      setFetchError('');
      try {
        const result = await billingService.createSetupIntent();
        setClientSecret(result.clientSecret);
        setSetupIntentStatus('requires_payment_method');
      } catch (error) {
        setSetupIntentStatus('error');
        setFetchError(error.message || 'Failed to initialize payment setup.');
      } finally {
        setIsRefreshingSetupIntent(false);
      }
    },
    [billingService]
  );

  useEffect(() => {
    clientSecretRef.current = clientSecret;
  }, [clientSecret]);

  useEffect(() => {
    setupIntentStatusRef.current = setupIntentStatus;
  }, [setupIntentStatus]);

  useEffect(() => {
    fetchSetupIntent();
  }, [fetchSetupIntent]);

  if (!clientSecret) {
    return (
      <Center h="80vh">
        {fetchError ? (
          <Alert status="error" maxW="400px">
            <AlertIcon />
            {fetchError}
          </Alert>
        ) : (
          <Spinner size="lg" />
        )}
      </Center>
    );
  }

  return (
    <StripeElementsProvider
      clientSecret={clientSecret}
      appearance={STRIPE_APPEARANCE}
    >
      <BillingFormFields
        selectedPlan={selectedPlan}
        setParentLoading={setParentLoading}
        companyName={companyName}
        accountEmail={accountEmail}
        isRefreshingSetupIntent={isRefreshingSetupIntent}
        refreshSetupIntent={() => fetchSetupIntent(true)}
      />
    </StripeElementsProvider>
  );
}

export default BillingForm;
